import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { CountrySelect } from '@/components/ui/country-select'
import {
  Pencil,
  AlertTriangleIcon,
  ShieldCheckIcon,
  LockIcon,
} from 'lucide-react'
import { BRAND_MINT, formatCurrencyAmount } from '@/lib/utils'
import {
  getCircleProfileApi,
  updateCircleProfileApi,
  getCountriesApi,
  type Country,
} from '@/lib/api'

const PAYMENT_CYCLES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'semi-monthly', label: 'Semi-monthly' },
  { value: 'monthly', label: 'Monthly' },
] as const


export const Route = createFileRoute('/dashboard/payroll-defaults')({
  component: () => {
    const [currency, setCurrency] = useState('')
    // maxAmount is stored in MONEY units (e.g. "1000.00") — the API speaks
    // cents (max_request_amount_allowed), so we convert at load (/100) and
    // save (×100). Mirrors the per-employee max in employee-settings.tsx.
    const [maxAmount, setMaxAmount] = useState('')
    const [maxCirclesPerUser, setMaxCirclesPerUser] = useState('')
    const [paymentCycle, setPaymentCycle] = useState('monthly')
    const [countryId, setCountryId] = useState<number | ''>('')
    const [countryName, setCountryName] = useState<string | null>(null)
    const [currencySymbol, setCurrencySymbol] = useState<string | null>(null)
    const [countries, setCountries] = useState<Country[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    // Option A: track whether the profile is missing required fields
    const [profileMissing, setProfileMissing] = useState(false)
    const [profileJustCompleted, setProfileJustCompleted] = useState(false)

    // Keep a ref of the last-saved values so cancelling reverts properly.
    const savedValues = useRef({
      currency: '',
      maxAmount: '',
      maxCirclesPerUser: '',
      paymentCycle: 'monthly',
      countryId: '' as number | '',
      countryName: null as string | null,
    })

    // Derive available currencies from ALL countries in the DB.
    const availableCurrencies = useMemo(() => {
      const seen = new Set<string>()
      const list: string[] = []
      for (const c of countries) {
        if (c.currency && !seen.has(c.currency)) {
          seen.add(c.currency)
          list.push(c.currency)
        }
      }
      return list.sort()
    }, [countries])

    // Compute dirty state by comparing current form values against last-saved
    const isDirty = useMemo(() => {
      if (!isEditing) return false
      const sv = savedValues.current
      return (
        currency !== sv.currency ||
        maxAmount !== sv.maxAmount ||
        maxCirclesPerUser !== sv.maxCirclesPerUser ||
        paymentCycle !== sv.paymentCycle ||
        countryId !== sv.countryId
      )
    }, [isEditing, currency, maxAmount, maxCirclesPerUser, paymentCycle, countryId])
    useUnsavedChangesWarning(isDirty)

    useEffect(() => {
      let cancelled = false

      async function load() {
        // Load countries first (independent of profile — must always populate
        // the Country select even when no profile exists yet)
        try {
          const countriesRes = await getCountriesApi()
          if (!cancelled) setCountries(countriesRes.data)
        } catch {
          if (!cancelled) {
            toast.error('Failed to load countries. Check that the API server is running.')
          }
        }

        // Load the circle profile (may 404 — that means "not configured")
        try {
          const profileRes = await getCircleProfileApi()
          if (cancelled) return

          const p = profileRes.profile
          if (p) {
            if (p.currency && p.currency.trim().length > 0) {
              setCurrency(p.currency)
            }
            if (p.max_request_amount_allowed) {
              setMaxAmount((p.max_request_amount_allowed / 100).toFixed(2))
            }
            if (p.max_circles_per_user !== null && p.max_circles_per_user !== undefined) {
              setMaxCirclesPerUser(String(p.max_circles_per_user))
            }
            if (p.payment_cycle) {
              if (!PAYMENT_CYCLES.some((pc) => pc.value === p.payment_cycle)) {
                console.warn(
                  '[PayrollDefaults] unexpected payment_cycle from API:',
                  p.payment_cycle,
                  '— falling back to monthly',
                )
              } else {
                setPaymentCycle(p.payment_cycle)
              }
            }
            if (p.country_id) {
              setCountryId(p.country_id)
              setCountryName(p.country_name)
            }
            if (p.currency_symbol) {
              setCurrencySymbol(p.currency_symbol)
            }

            // Sync savedValues with the freshly loaded profile so
            // cancelling reverts to what the API returned.
            savedValues.current = {
              currency: p.currency || '',
              maxAmount: p.max_request_amount_allowed
                ? (p.max_request_amount_allowed / 100).toFixed(2)
                : '',
              maxCirclesPerUser:
                p.max_circles_per_user !== null && p.max_circles_per_user !== undefined
                  ? String(p.max_circles_per_user)
                  : '',
              paymentCycle:
                p.payment_cycle && PAYMENT_CYCLES.some((pc) => pc.value === p.payment_cycle)
                  ? p.payment_cycle
                  : 'monthly',
              countryId: p.country_id ?? '',
              countryName: p.country_name ?? null,
            }

            // Check if profile is missing required fields
            const isComplete =
              p.country_id != null &&
              p.currency &&
              p.currency.trim().length > 0 &&
              p.payment_cycle &&
              p.payment_cycle.trim().length > 0

            if (!isComplete) {
              setProfileMissing(true)
              setIsEditing(true)
            }
          }
        } catch {
          // No profile exists (404) — mark as missing and auto-enter edit mode
          if (!cancelled) {
            setProfileMissing(true)
            setIsEditing(true)
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }

      load()
      return () => {
        cancelled = true
      }
    }, [])

    const handleSave = async () => {
      setSaving(true)
      try {
        await updateCircleProfileApi({
          currency,
          max_request_amount_allowed: maxAmount ? Math.round(parseFloat(maxAmount) * 100) : undefined,
          max_circles_per_user: maxCirclesPerUser
            ? Number(maxCirclesPerUser)
            : null, // null = no limit
          payment_cycle: paymentCycle,
          country_id: countryId ? Number(countryId) : undefined,
        })
        // Update countryName and currency symbol after save using the selected country
        const selectedCountry = countries.find((c) => c.country_id === countryId)
        const savedSymbol = selectedCountry?.currency_symbol ?? null
        savedValues.current = {
          currency,
          maxAmount,
          maxCirclesPerUser,
          paymentCycle,
          countryId,
          countryName: selectedCountry?.country ?? null,
        }
        if (selectedCountry) {
          setCountryName(selectedCountry.country)
        }
        setCurrencySymbol(savedSymbol)
        // Check if the saved profile is now complete
        const isNowComplete =
          currency && currency.trim().length > 0 &&
          paymentCycle && paymentCycle.trim().length > 0 &&
          countryId != null && countryId !== ''

        if (profileMissing && isNowComplete) {
          setProfileJustCompleted(true)
        }
        setProfileMissing(false)
        setIsEditing(false)
        toast.success('Payroll defaults saved')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save')
      } finally {
        setSaving(false)
      }
    }

    const handleCancel = () => {
      // Revert to last-saved values
      setCurrency(savedValues.current.currency)
      setMaxAmount(savedValues.current.maxAmount)
      setMaxCirclesPerUser(savedValues.current.maxCirclesPerUser)
      setPaymentCycle(savedValues.current.paymentCycle)
      setCountryId(savedValues.current.countryId)
      setCountryName(savedValues.current.countryName)
      setCurrencySymbol(null)
      setIsEditing(false)
    }

    const handleEdit = () => {
      setIsEditing(true)
    }

    // --------------- View helpers ---------------

    const paymentCycleLabel =
      PAYMENT_CYCLES.find((pc) => pc.value === paymentCycle)?.label ?? paymentCycle

    const maxAmountDisplay = maxAmount
      ? formatCurrencyAmount(Number(maxAmount), currency || 'USD', currencySymbol)
      : 'No limit'

    const maxCirclesDisplay = maxCirclesPerUser ? maxCirclesPerUser : 'No limit'

    // --------------- Loading state ---------------

    if (loading) {
      return (
        <div className="mx-auto max-w-2xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <h1 className="text-2xl font-semibold">Payroll defaults</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <p className="text-sm text-muted-foreground">Loading defaults…</p>
          </div>
        </div>
      )
    }

    // --------------- View mode ---------------

    if (!isEditing) {
      return (
        <div className="mx-auto max-w-2xl">
          {/* Option A: success banner after completing profile */}
          {profileJustCompleted && (
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800/40 dark:bg-emerald-950/20">
              <ShieldCheckIcon className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div>
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                  Payroll setup complete!
                </p>
                <p className="mt-0.5 text-sm text-emerald-700 dark:text-emerald-400">
                  Your organization is now configured. Employees can create Funding Circles,
                  and all features are unlocked.
                </p>
              </div>
            </div>
          )}

          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 shrink-0"
                style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
              />
              <div>
                <h1 className="text-2xl font-semibold">Payroll defaults</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Configure the currency, per-cycle maximum, and Circle participation
                  limits that apply to all employees. Per-employee pay schedules live
                  in{' '}
                  <Link
                    to="/dashboard/employee-settings"
                    className="font-medium text-primary underline underline-offset-4 hover:opacity-80"
                  >
                    Employees → Settings
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>

          <section className="space-y-6">
            <div className="divide-y divide-border rounded-xl border border-border bg-card">
              <div className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Country</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{countryName ?? '—'}</p>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Currency</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {currency || '—'} {currencySymbol ? `(${currencySymbol})` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Maximum per participant (per cycle)</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{maxAmountDisplay}</p>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Max active Circles per employee</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{maxCirclesDisplay}</p>
                </div>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">Payment cycle</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{paymentCycleLabel}</p>
                </div>
              </div>
            </div>
          </section>

          <div className="flex items-center gap-3 pt-6">
            <Button onClick={handleEdit}>
              <Pencil className="mr-2 size-4 text-[var(--brand-mint)]" />
              Edit defaults
            </Button>
          </div>
        </div>
      )
    }

    // --------------- Edit mode ---------------

    return (
      <div className="mx-auto max-w-2xl">
        {/* Option A: Banner for incomplete profile */}
        {profileMissing && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/40 dark:bg-amber-950/20">
            <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Payroll setup required
              </p>
              <p className="mt-0.5 text-sm text-amber-700 dark:text-amber-400">
                You must configure your <strong>country</strong>,{' '}
                <strong>currency</strong>, and <strong>payment cycle</strong> before
                employees can create Funding Circles and your organization can process
                payroll. Other features will remain locked until this is completed.
              </p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-2xl font-semibold">Payroll defaults</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure the currency, per-cycle maximum, and Circle participation
            limits that apply to all employees. Per-employee pay schedules live
            in{' '}
            <Link
              to="/dashboard/employee-settings"
              className="font-medium text-primary underline underline-offset-4 hover:opacity-80"
            >
              Employees → Settings
            </Link>
            .
          </p>
        </div>

        {/* Option A: feature-lock notice when profile is missing */}
        {profileMissing && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-border bg-muted/50 p-4">
            <LockIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium text-foreground">
                What's locked until you finish?
              </p>
              <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground space-y-0.5">
                <li>Employees cannot create Funding Circle requests</li>
                <li>Payment cycles cannot be initiated</li>
                <li>Payouts cannot be completed</li>
              </ul>
            </div>
          </div>
        )}

        <section className="space-y-6">
          <div className="grid gap-6 rounded-xl border border-border bg-card p-4">
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <CountrySelect
                id="country"
                value={countryId}
                onChange={(id) => {
                  setCountryId(id)
                  // Auto-select the country's currency and symbol when country changes
                  const c = countries.find((x) => x.country_id === id)
                  if (c && c.currency) {
                    setCurrency(c.currency)
                    setCurrencySymbol(c.currency_symbol ?? null)
                  }
                }}
                countries={countries}
                disabled={saving}
                emptyLabel="Select a country"
              />
              <p className="text-xs text-muted-foreground">
                The country where your organization operates. Used for regional compliance and defaults.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={saving}
              >
                {availableCurrencies.map((c) => {
                  const country = countries.find((x) => x.currency === c)
                  const sym = country?.currency_symbol ?? ''
                  return (
                    <option key={c} value={c}>
                      {c}{sym ? ` (${sym})` : ''}
                    </option>
                  )
                })}
              </Select>
              <p className="text-xs text-muted-foreground">
                Payroll cycles and pending payments display amounts in this
                currency.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxAmount">Maximum per participant (per cycle)</Label>
              <Input
                id="maxAmount"
                type="number"
                inputMode="numeric"
                min={0}
                step="0.01"
                placeholder="No limit"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Caps the per-participant share that employees can request on
                any single cycle. Leave empty for no cap.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="maxCirclesPerUser">
                Max active Circles per employee
              </Label>
              <Input
                id="maxCirclesPerUser"
                type="number"
                inputMode="numeric"
                min={0}
                step="1"
                placeholder="No limit"
                value={maxCirclesPerUser}
                onChange={(e) => setMaxCirclesPerUser(e.target.value)}
                disabled={saving}
              />
              <p className="text-xs text-muted-foreground">
                Limits how many active (open or full) Circles each employee
                can join at the same time. Leave empty for no limit.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentCycle">Payment cycle</Label>
              <Select
                id="paymentCycle"
                value={paymentCycle}
                onChange={(e) => setPaymentCycle(e.target.value)}
                disabled={saving}
              >
                {PAYMENT_CYCLES.map((pc) => (
                  <option key={pc.value} value={pc.value}>
                    {pc.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                Sets the payout schedule for all new Funding Circles created by
                employees. Changes only apply to future Circles.
              </p>
            </div>
          </div>
        </section>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <Button variant="outline" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    )
  },
})
